import { apiFetch, apiFetchBlob } from "@/lib/api";

/**
 * API layer for the global reference/lookup data (Settings → Reference Data).
 * `apiFetch` returns the Django envelope verbatim; this reads `.data`/`.meta`
 * and maps the snake_case API shape to camelCase (the settled per-feature
 * convention). Money fields stay **strings** end-to-end — never parsed to a
 * float — so decimal precision is preserved on display and round-trip.
 */

interface Envelope<T> {
  data: T;
}
interface ListEnvelope<T> {
  data: T[];
  meta: { count: number; next: string | null; previous: string | null };
}

export interface ListResult<T> {
  items: T[];
  count: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

async function getList<A, T>(path: string, map: (a: A) => T): Promise<ListResult<T>> {
  const { data, meta } = await apiFetch<ListEnvelope<A>>(path);
  return {
    items: data.map(map),
    count: meta.count,
    hasNext: !!meta.next,
    hasPrevious: !!meta.previous,
  };
}

// --- Notice definitions -----------------------------------------------------

export type NoticeSeverity = "INFO" | "ACTION_REQUIRED" | "URGENT";
export type NoticeCategory =
  | "BALANCE_DUE"
  | "UNDERREPORTED_INCOME"
  | "COLLECTION"
  | "LIEN_LEVY"
  | "AUDIT"
  | "REFUND"
  | "IDENTITY"
  | "GENERAL";

export interface NoticeDefinition {
  id: string;
  code: string;
  title: string;
  description: string;
  severity: NoticeSeverity;
  category: NoticeCategory;
  recommendedAction: string;
  isActive: boolean;
}
export interface NoticePayload {
  code: string;
  title: string;
  description: string;
  severity: NoticeSeverity;
  category: NoticeCategory;
  recommendedAction: string;
  isActive: boolean;
}
interface ApiNotice {
  id: string;
  code: string;
  title: string;
  description: string;
  severity: NoticeSeverity;
  category: NoticeCategory;
  recommended_action: string;
  is_active: boolean;
}
const toNotice = (a: ApiNotice): NoticeDefinition => ({
  id: a.id,
  code: a.code,
  title: a.title,
  description: a.description,
  severity: a.severity,
  category: a.category,
  recommendedAction: a.recommended_action,
  isActive: a.is_active,
});
const noticeBody = (p: NoticePayload) => ({
  code: p.code,
  title: p.title,
  description: p.description,
  severity: p.severity,
  category: p.category,
  recommended_action: p.recommendedAction,
  is_active: p.isActive,
});

export const NOTICE_SEVERITY_OPTIONS: { value: NoticeSeverity; label: string }[] = [
  { value: "INFO", label: "Informational" },
  { value: "ACTION_REQUIRED", label: "Action required" },
  { value: "URGENT", label: "Urgent" },
];
export const NOTICE_CATEGORY_OPTIONS: { value: NoticeCategory; label: string }[] = [
  { value: "BALANCE_DUE", label: "Balance due" },
  { value: "UNDERREPORTED_INCOME", label: "Underreported income" },
  { value: "COLLECTION", label: "Collection" },
  { value: "LIEN_LEVY", label: "Lien / levy" },
  { value: "AUDIT", label: "Audit" },
  { value: "REFUND", label: "Refund" },
  { value: "IDENTITY", label: "Identity" },
  { value: "GENERAL", label: "General" },
];

export function listNotices(params: {
  search?: string;
  category?: string;
  severity?: string;
  active?: string;
  page?: number;
  pageSize?: number;
}): Promise<ListResult<NoticeDefinition>> {
  return getList<ApiNotice, NoticeDefinition>(
    `/reference/notices/${qs({
      search: params.search,
      category: params.category,
      severity: params.severity,
      active: params.active,
      page: params.page,
      page_size: params.pageSize,
    })}`,
    toNotice,
  );
}
export async function createNotice(p: NoticePayload): Promise<NoticeDefinition> {
  const { data } = await apiFetch<Envelope<ApiNotice>>("/reference/notices/", {
    method: "POST",
    json: noticeBody(p),
  });
  return toNotice(data);
}
export async function updateNotice(id: string, p: NoticePayload): Promise<NoticeDefinition> {
  const { data } = await apiFetch<Envelope<ApiNotice>>(`/reference/notices/${id}/`, {
    method: "PATCH",
    json: noticeBody(p),
  });
  return toNotice(data);
}
export function deleteNotice(id: string): Promise<void> {
  return apiFetch<void>(`/reference/notices/${id}/`, { method: "DELETE" });
}

// --- Tax forms --------------------------------------------------------------

export type TaxFormCategory =
  | "COLLECTION_INFO"
  | "POA_AUTHORIZATION"
  | "PAYMENT_PLAN"
  | "OFFER_IN_COMPROMISE"
  | "RELIEF"
  | "APPEALS"
  | "OTHER";

export interface TaxForm {
  id: string;
  formNumber: string;
  title: string;
  category: TaxFormCategory;
  appliesToIndividuals: boolean;
  appliesToBusinesses: boolean;
  isActive: boolean;
  fileName: string;
  irsTitle: string;
}
export interface TaxFormPayload {
  formNumber: string;
  title: string;
  category: TaxFormCategory;
  appliesToIndividuals: boolean;
  appliesToBusinesses: boolean;
  isActive: boolean;
  fileName: string;
}
interface ApiTaxForm {
  id: string;
  form_number: string;
  title: string;
  category: TaxFormCategory;
  applies_to_individuals: boolean;
  applies_to_businesses: boolean;
  is_active: boolean;
  file_name: string;
  irs_title: string;
}
const toTaxForm = (a: ApiTaxForm): TaxForm => ({
  id: a.id,
  formNumber: a.form_number,
  title: a.title,
  category: a.category,
  appliesToIndividuals: a.applies_to_individuals,
  appliesToBusinesses: a.applies_to_businesses,
  isActive: a.is_active,
  fileName: a.file_name,
  irsTitle: a.irs_title,
});
const taxFormBody = (p: TaxFormPayload) => ({
  form_number: p.formNumber,
  title: p.title,
  category: p.category,
  applies_to_individuals: p.appliesToIndividuals,
  applies_to_businesses: p.appliesToBusinesses,
  is_active: p.isActive,
  file_name: p.fileName,
});

export const TAX_FORM_CATEGORY_OPTIONS: { value: TaxFormCategory; label: string }[] = [
  { value: "COLLECTION_INFO", label: "Collection information" },
  { value: "POA_AUTHORIZATION", label: "POA / authorization" },
  { value: "PAYMENT_PLAN", label: "Payment plan" },
  { value: "OFFER_IN_COMPROMISE", label: "Offer in compromise" },
  { value: "RELIEF", label: "Relief / abatement" },
  { value: "APPEALS", label: "Appeals" },
  { value: "OTHER", label: "Other" },
];

export function listTaxForms(params: {
  search?: string;
  category?: string;
  appliesTo?: string;
  active?: string;
  page?: number;
  pageSize?: number;
}): Promise<ListResult<TaxForm>> {
  return getList<ApiTaxForm, TaxForm>(
    `/reference/tax-forms/${qs({
      search: params.search,
      category: params.category,
      applies_to: params.appliesTo,
      active: params.active,
      page: params.page,
      page_size: params.pageSize,
    })}`,
    toTaxForm,
  );
}
export async function createTaxForm(p: TaxFormPayload): Promise<TaxForm> {
  const { data } = await apiFetch<Envelope<ApiTaxForm>>("/reference/tax-forms/", {
    method: "POST",
    json: taxFormBody(p),
  });
  return toTaxForm(data);
}
export async function updateTaxForm(id: string, p: Partial<TaxFormPayload>): Promise<TaxForm> {
  const body: Record<string, unknown> = {};
  if (p.formNumber !== undefined) body.form_number = p.formNumber;
  if (p.title !== undefined) body.title = p.title;
  if (p.category !== undefined) body.category = p.category;
  if (p.appliesToIndividuals !== undefined) body.applies_to_individuals = p.appliesToIndividuals;
  if (p.appliesToBusinesses !== undefined) body.applies_to_businesses = p.appliesToBusinesses;
  if (p.isActive !== undefined) body.is_active = p.isActive;
  if (p.fileName !== undefined) body.file_name = p.fileName;
  const { data } = await apiFetch<Envelope<ApiTaxForm>>(`/reference/tax-forms/${id}/`, {
    method: "PATCH",
    json: body,
  });
  return toTaxForm(data);
}
export function deleteTaxForm(id: string): Promise<void> {
  return apiFetch<void>(`/reference/tax-forms/${id}/`, { method: "DELETE" });
}

// --- Collection standards ---------------------------------------------------

export type StandardType =
  | "NATIONAL_EXPENSE"
  | "HEALTHCARE_OOP"
  | "VEHICLE_OWNERSHIP"
  | "ASSET_EXEMPTION"
  | "PUBLIC_TRANSIT"
  | "VEHICLE_OPERATING";

export interface CollectionStandard {
  id: string;
  standardType: StandardType;
  item: string;
  householdSize: number | null;
  monthlyAmount: string;
  effectiveDate: string;
}
interface ApiCollectionStandard {
  id: string;
  standard_type: StandardType;
  item: string;
  household_size: number | null;
  monthly_amount: string;
  effective_date: string;
}
const toStandard = (a: ApiCollectionStandard): CollectionStandard => ({
  id: a.id,
  standardType: a.standard_type,
  item: a.item,
  householdSize: a.household_size,
  monthlyAmount: a.monthly_amount,
  effectiveDate: a.effective_date,
});

export interface StandardRow {
  standardType: StandardType;
  item: string;
  householdSize: number | null;
  monthlyAmount: string;
}

export function listStandards(params: {
  standardType?: string;
  effectiveOn?: string;
  search?: string;
  pageSize?: number;
}): Promise<ListResult<CollectionStandard>> {
  return getList<ApiCollectionStandard, CollectionStandard>(
    `/reference/collection-standards/${qs({
      standard_type: params.standardType,
      effective_on: params.effectiveOn,
      search: params.search,
      page_size: params.pageSize ?? 200,
    })}`,
    toStandard,
  );
}

/** Insert a new vintage (national-expense grid / other-allowance / transit save). */
export async function createStandardVintage(
  effectiveDate: string,
  rows: StandardRow[],
): Promise<void> {
  await apiFetch("/reference/collection-standards/bulk/", {
    method: "POST",
    json: {
      effective_date: effectiveDate,
      rows: rows.map((r) => ({
        standard_type: r.standardType,
        item: r.item,
        household_size: r.householdSize,
        monthly_amount: r.monthlyAmount,
      })),
    },
  });
}

export function exportAllowances(effectiveOn?: string): Promise<Blob> {
  return apiFetchBlob(`/reference/collection-standards/export/${qs({ effective_on: effectiveOn })}`);
}

export async function importAllowances(file: File): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  await apiFetch("/reference/collection-standards/import_json/", { method: "POST", form });
}

// --- Local housing ----------------------------------------------------------

export interface HousingStandard {
  id: string;
  state: string;
  county: string;
  householdSize: number;
  monthlyAmount: string;
  effectiveDate: string;
}
interface ApiHousingStandard {
  id: string;
  state: string;
  county: string;
  household_size: number;
  monthly_amount: string;
  effective_date: string;
}
const toHousing = (a: ApiHousingStandard): HousingStandard => ({
  id: a.id,
  state: a.state,
  county: a.county,
  householdSize: a.household_size,
  monthlyAmount: a.monthly_amount,
  effectiveDate: a.effective_date,
});

export function listHousing(params: {
  state?: string;
  county?: string;
  pageSize?: number;
}): Promise<ListResult<HousingStandard>> {
  return getList<ApiHousingStandard, HousingStandard>(
    `/reference/housing-standards/${qs({
      state: params.state,
      county: params.county,
      page_size: params.pageSize ?? 500,
    })}`,
    toHousing,
  );
}
export async function updateHousing(
  id: string,
  monthlyAmount: string,
): Promise<HousingStandard> {
  const { data } = await apiFetch<Envelope<ApiHousingStandard>>(
    `/reference/housing-standards/${id}/`,
    { method: "PATCH", json: { monthly_amount: monthlyAmount } },
  );
  return toHousing(data);
}
export async function importHousing(
  file: File,
  effectiveDate: string,
  mode: "append" | "replace",
): Promise<{ rowsImported: number; counties: number }> {
  const form = new FormData();
  form.append("file", file);
  form.append("effective_date", effectiveDate);
  form.append("mode", mode);
  const { data } = await apiFetch<Envelope<{ rows_imported: number; counties: number }>>(
    "/reference/housing-standards/import_csv/",
    { method: "POST", form },
  );
  return { rowsImported: data.rows_imported, counties: data.counties };
}

// --- Vehicle operating regions ----------------------------------------------

export interface VehicleRegion {
  id: string;
  region: string;
  oneCarMonthly: string;
  twoCarsMonthly: string;
  effectiveDate: string;
}
interface ApiVehicleRegion {
  id: string;
  region: string;
  one_car_monthly: string;
  two_cars_monthly: string;
  effective_date: string;
}
const toVehicleRegion = (a: ApiVehicleRegion): VehicleRegion => ({
  id: a.id,
  region: a.region,
  oneCarMonthly: a.one_car_monthly,
  twoCarsMonthly: a.two_cars_monthly,
  effectiveDate: a.effective_date,
});

export function listVehicleRegions(effectiveOn?: string): Promise<ListResult<VehicleRegion>> {
  return getList<ApiVehicleRegion, VehicleRegion>(
    `/reference/vehicle-operating-regions/${qs({ effective_on: effectiveOn, page_size: 200 })}`,
    toVehicleRegion,
  );
}
export async function createVehicleRegion(p: {
  region: string;
  oneCarMonthly: string;
  twoCarsMonthly: string;
  effectiveDate: string;
}): Promise<VehicleRegion> {
  const { data } = await apiFetch<Envelope<ApiVehicleRegion>>(
    "/reference/vehicle-operating-regions/",
    {
      method: "POST",
      json: {
        region: p.region,
        one_car_monthly: p.oneCarMonthly,
        two_cars_monthly: p.twoCarsMonthly,
        effective_date: p.effectiveDate,
      },
    },
  );
  return toVehicleRegion(data);
}
export async function updateVehicleRegion(
  id: string,
  p: { oneCarMonthly: string; twoCarsMonthly: string },
): Promise<VehicleRegion> {
  const { data } = await apiFetch<Envelope<ApiVehicleRegion>>(
    `/reference/vehicle-operating-regions/${id}/`,
    {
      method: "PATCH",
      json: { one_car_monthly: p.oneCarMonthly, two_cars_monthly: p.twoCarsMonthly },
    },
  );
  return toVehicleRegion(data);
}

export interface RegionCounty {
  id: string;
  state: string;
  county: string;
  region: string;
}
interface ApiRegionCounty {
  id: string;
  state: string;
  county: string;
  region: string;
}
export function listRegionCounties(params: {
  state?: string;
  region?: string;
}): Promise<ListResult<RegionCounty>> {
  return getList<ApiRegionCounty, RegionCounty>(
    `/reference/region-counties/${qs({ state: params.state, region: params.region, page_size: 500 })}`,
    (a) => a,
  );
}

// --- IRS defaults (singleton) -----------------------------------------------

export interface IrsDefaults {
  interestRate: string;
  passportThreshold: string;
  vehicleExtraMiles: number;
  vehicleExtraYears: number;
  vehicleExtraAmount: string;
}
interface ApiIrsDefaults {
  interest_rate: string;
  passport_threshold: string;
  vehicle_extra_miles: number;
  vehicle_extra_years: number;
  vehicle_extra_amount: string;
}
const toDefaults = (a: ApiIrsDefaults): IrsDefaults => ({
  interestRate: a.interest_rate,
  passportThreshold: a.passport_threshold,
  vehicleExtraMiles: a.vehicle_extra_miles,
  vehicleExtraYears: a.vehicle_extra_years,
  vehicleExtraAmount: a.vehicle_extra_amount,
});
export async function getIrsDefaults(): Promise<IrsDefaults> {
  const { data } = await apiFetch<Envelope<ApiIrsDefaults>>("/reference/irs-defaults/");
  return toDefaults(data);
}
export async function updateIrsDefaults(p: IrsDefaults): Promise<IrsDefaults> {
  const { data } = await apiFetch<Envelope<ApiIrsDefaults>>("/reference/irs-defaults/", {
    method: "PATCH",
    json: {
      interest_rate: p.interestRate,
      passport_threshold: p.passportThreshold,
      vehicle_extra_miles: p.vehicleExtraMiles,
      vehicle_extra_years: p.vehicleExtraYears,
      vehicle_extra_amount: p.vehicleExtraAmount,
    },
  });
  return toDefaults(data);
}

// --- Transaction codes + groups ---------------------------------------------

export type TransactionCategory =
  | "ASSESSMENT"
  | "PAYMENT_CREDIT"
  | "PENALTY"
  | "INTEREST"
  | "LEGAL_ACTION"
  | "OFFER_IN_COMPROMISE"
  | "INSTALLMENT_AGREEMENT"
  | "NOTICE"
  | "MISC";

export interface TransactionCode {
  id: string;
  code: string;
  description: string;
  category: TransactionCategory;
}
export interface TransactionCodePayload {
  code: string;
  description: string;
  category: TransactionCategory;
}
export const TRANSACTION_CATEGORY_OPTIONS: { value: TransactionCategory; label: string }[] = [
  { value: "ASSESSMENT", label: "Assessment" },
  { value: "PAYMENT_CREDIT", label: "Payment / credit" },
  { value: "PENALTY", label: "Penalty" },
  { value: "INTEREST", label: "Interest" },
  { value: "LEGAL_ACTION", label: "Legal action" },
  { value: "OFFER_IN_COMPROMISE", label: "Offer in compromise" },
  { value: "INSTALLMENT_AGREEMENT", label: "Installment agreement" },
  { value: "NOTICE", label: "Notice" },
  { value: "MISC", label: "Miscellaneous" },
];

export function listTransactionCodes(params: {
  search?: string;
  category?: string;
  page?: number;
  pageSize?: number;
}): Promise<ListResult<TransactionCode>> {
  return getList<TransactionCode, TransactionCode>(
    `/reference/transaction-codes/${qs({
      search: params.search,
      category: params.category,
      page: params.page,
      page_size: params.pageSize,
    })}`,
    (a) => a,
  );
}
export async function createTransactionCode(
  p: TransactionCodePayload,
): Promise<TransactionCode> {
  const { data } = await apiFetch<Envelope<TransactionCode>>("/reference/transaction-codes/", {
    method: "POST",
    json: p,
  });
  return data;
}
export async function updateTransactionCode(
  id: string,
  p: TransactionCodePayload,
): Promise<TransactionCode> {
  const { data } = await apiFetch<Envelope<TransactionCode>>(
    `/reference/transaction-codes/${id}/`,
    { method: "PATCH", json: p },
  );
  return data;
}
export function deleteTransactionCode(id: string): Promise<void> {
  return apiFetch<void>(`/reference/transaction-codes/${id}/`, { method: "DELETE" });
}

export interface TransactionCodeGroup {
  id: string;
  key: string;
  label: string;
  codes: string[]; // transaction-code ids
}
interface ApiTransactionCodeGroup {
  id: string;
  key: string;
  label: string;
  codes: string[];
}
export async function listTransactionCodeGroups(): Promise<TransactionCodeGroup[]> {
  const { items } = await getList<ApiTransactionCodeGroup, TransactionCodeGroup>(
    `/reference/transaction-code-groups/${qs({ page_size: 200 })}`,
    (a) => a,
  );
  return items;
}
export async function updateGroupCodes(
  id: string,
  codeIds: string[],
): Promise<TransactionCodeGroup> {
  const { data } = await apiFetch<Envelope<ApiTransactionCodeGroup>>(
    `/reference/transaction-code-groups/${id}/`,
    { method: "PATCH", json: { codes: codeIds } },
  );
  return data;
}

// --- Transcript types (stub) ------------------------------------------------

export interface TranscriptType {
  id: string;
  transType: string;
  shortDesc: string;
  appliesToIndividuals: boolean;
  appliesToBusinesses: boolean;
  monitor: boolean;
  download: boolean;
}
interface ApiTranscriptType {
  id: string;
  trans_type: string;
  short_desc: string;
  applies_to_individuals: boolean;
  applies_to_businesses: boolean;
  monitor: boolean;
  download: boolean;
}
export async function listTranscriptTypes(): Promise<TranscriptType[]> {
  const { items } = await getList<ApiTranscriptType, TranscriptType>(
    `/reference/transcript-types/${qs({ page_size: 100 })}`,
    (a) => ({
      id: a.id,
      transType: a.trans_type,
      shortDesc: a.short_desc,
      appliesToIndividuals: a.applies_to_individuals,
      appliesToBusinesses: a.applies_to_businesses,
      monitor: a.monitor,
      download: a.download,
    }),
  );
  return items;
}
